import { useDropzone } from 'react-dropzone';

import { Fade, Flex, Box, type BoxProps } from '@frontend/components';

import UploadButton, {
  type UploadFilesPayload,
  type UploadButtonProps,
  type UploadCategory,
} from '../UploadButton/UploadButton';

type UseOverlay = { zIndex: number | null };

export type PartUploaderPayload =
  | UploadFilesPayload
  | {
      file: File | File[];
      units?: string;
      groupId?: string;
      category?: string;
    };

type DropzoneWrapperProps = {
  onUpload: (files: File[]) => void;
  children: React.ReactNode;
  useOverlay?: UseOverlay | false | null;
  activeClassName?: string;
  disabled?: boolean;
} & BoxProps;

const DropzoneWrapper = ({
  onUpload,
  children,
  useOverlay,
  activeClassName: _,
  disabled,
  ...props
}: DropzoneWrapperProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onUpload,
    noClick: true,
    noKeyboard: true,
    disabled,
  });

  return (
    <Box
      {...getRootProps()}
      style={{}}
      flex={1}
      maxWidth="100%"
      position="relative"
      {...props}
    >
      {children}
      <input {...getInputProps()} />
      {useOverlay ? (
        <Flex
          as={Fade}
          in={isDragActive}
          unmountOnExit
          position="absolute"
          top="0"
          left="0"
          w="100%"
          h="100%"
          backgroundColor="rgba(0, 0, 0, 0.7)"
          color="white"
          justify="center"
          align="center"
          zIndex={useOverlay.zIndex ?? 'unset'}
        >
          <h2>Drop Files Here</h2>
        </Flex>
      ) : null}
    </Box>
  );
};

type PartUploaderCoreProps = {
  children: React.ReactNode;
  inline?: boolean;
  multiple?: boolean;
  defaultUnits?: string;
  groupId?: string;
  category?: UploadCategory;
};

type PartUploaderUploadProps =
  | {
      supportingFileUpload?: false;
      onUpload: (files: PartUploaderPayload) => void;
    }
  | {
      supportingFileUpload: true;
      onUpload: (files: UploadFilesPayload) => void;
    };

type PartUploaderTypeProps =
  | ({ type: 'dropzone' } & DropzoneWrapperProps)
  | ({ type: 'button' } & UploadButtonProps);

export type PartUploaderProps = PartUploaderCoreProps &
  PartUploaderUploadProps &
  PartUploaderTypeProps;

const PartUploader = ({ children, inline, ...props }: PartUploaderProps) => {
  const {
    type = 'dropzone',
    supportingFileUpload,
    onUpload,
    multiple = false,
    defaultUnits,
    groupId,
    category,
    ...rest
  } = props;
  const handleUpload = (files: UploadFilesPayload) => {
    supportingFileUpload
      ? onUpload(files)
      : onUpload({
          file: (multiple ? files : Array.isArray(files) ? files[0] : files) as
            | File
            | File[],
          units: defaultUnits,
          groupId: groupId,
          ...(category && { category }),
        });
  };

  return (
    <Flex flex={1} maxWidth="100%" display={inline ? 'inline-flex' : 'flex'}>
      {type === 'button' ? (
        <UploadButton
          {...(rest as UploadButtonProps)}
          multiple={multiple}
          category={supportingFileUpload ? category : undefined}
          onUpload={handleUpload}
        >
          <>{children}</>
        </UploadButton>
      ) : (
        <DropzoneWrapper
          {...(rest as DropzoneWrapperProps)}
          onUpload={handleUpload}
        >
          <>{children}</>
        </DropzoneWrapper>
      )}
    </Flex>
  );
};

PartUploader.displayName = 'PartUploader';

export default PartUploader;
